/**
 * Cookie Storage Adapter pour Supabase Auth
 * 
 * Remplace localStorage pour la persistance de session Supabase.
 * Utilise un cookie fonctionnel partage sur magicparktimes.com afin que
 * weather.magicparktimes.com puisse réutiliser la session membre.
 * 
 * Ce cookie est strictement fonctionnel (maintien de session),
 * il n'est pas soumis au consentement RGPD (pas de tracking).
 */

const COOKIE_NAME = 'mpt_auth_session';
const COOKIE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;
const COOKIE_CHUNKS_NAME = `${COOKIE_NAME}_chunks`;
const COOKIE_CHUNK_PREFIX = `${COOKIE_NAME}_chunk_`;
const MAX_COOKIE_CHUNK_SIZE = 3500;
const SHARED_COOKIE_DOMAIN = '.magicparktimes.com';

const shouldUseSharedCookieDomain = () => {
  try {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    return hostname === 'magicparktimes.com'
      || hostname === 'www.magicparktimes.com'
      || hostname.endsWith('.magicparktimes.com');
  } catch {
    return false;
  }
};
/**
 * Encode une valeur pour stockage dans un cookie (base64 pour éviter les problèmes de caractères).
 * Compatible navigateur et Node.js/SSR.
 */
const encodeValue = (value) => {
  try {
    // Navigateur : btoa
    if (typeof globalThis !== 'undefined' && typeof globalThis.btoa === 'function') {
      return globalThis.btoa(unescape(encodeURIComponent(value)));
    }
    // Node.js/SSR : Buffer
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(value, 'utf8').toString('base64');
    }
    // Fallback
    return value;
  } catch {
    return value;
  }
};

/**
 * Décode une valeur lue depuis un cookie.
 * Compatible navigateur et Node.js/SSR.
 */
const decodeValue = (encoded) => {
  try {
    let decoded = null;

    // Navigateur : atob
    if (typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function') {
      decoded = globalThis.atob(encoded);
      try {
        decoded = decodeURIComponent(escape(decoded));
      } catch {
        // ignore
      }
    } else if (typeof Buffer !== 'undefined') {
      // Node.js/SSR : Buffer
      decoded = Buffer.from(encoded, 'base64').toString('utf8');
    } else {
      decoded = encoded;
    }

    // Rétro-compat : ancien format = base64(encodeURIComponent(value))
    // Après base64 decode, on obtient une chaîne avec des %xx.
    if (typeof decoded === 'string' && /%[0-9A-Fa-f]{2}/.test(decoded)) {
      try {
        return decodeURIComponent(decoded);
      } catch {
        // ignore
      }
    }

    return decoded;
  } catch {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
};

/**
 * Lit un cookie par son nom.
 */
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, ...cookieValueParts] = cookie.split('=');
    if (cookieName.trim() === name) {
      return cookieValueParts.join('=').trim();
    }
  }
  return null;
};

const deleteChunkCookies = () => {
  try {
    const rawCount = getCookie(COOKIE_CHUNKS_NAME);
    const count = rawCount ? Number.parseInt(rawCount, 10) : 0;
    if (Number.isFinite(count) && count > 0) {
      for (let i = 0; i < count; i++) {
        deleteCookie(`${COOKIE_CHUNK_PREFIX}${i}`);
      }
    }
  } catch {
    // ignore
  }

  // Fallback : si le compteur n'existe pas mais que des chunks traînent,
  // on supprime tous les cookies chunk_* trouvés.
  try {
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [cookieName] = cookie.split('=');
        const name = (cookieName || '').trim();
        if (name.startsWith(COOKIE_CHUNK_PREFIX)) {
          deleteCookie(name);
        }
      }
    }
  } catch {
    // ignore
  }

  deleteCookie(COOKIE_CHUNKS_NAME);
  deleteCookie(COOKIE_NAME);
};

const readCookiePayload = () => {
  const rawCount = getCookie(COOKIE_CHUNKS_NAME);
  const count = rawCount ? Number.parseInt(rawCount, 10) : 0;

  if (Number.isFinite(count) && count > 0) {
    let assembled = '';
    for (let i = 0; i < count; i++) {
      const part = getCookie(`${COOKIE_CHUNK_PREFIX}${i}`);
      if (!part) {
        // Payload tronqué / incohérent : on nettoie pour repartir proprement
        deleteChunkCookies();
        return null;
      }
      assembled += part;
    }
    return assembled;
  }

  return getCookie(COOKIE_NAME);
};

const writeCookiePayload = (payload) => {
  deleteChunkCookies();

  if (!payload) return;

  if (payload.length <= MAX_COOKIE_CHUNK_SIZE) {
    setCookie(COOKIE_NAME, payload, COOKIE_MAX_AGE_SECONDS);
    return;
  }

  const parts = [];
  for (let i = 0; i < payload.length; i += MAX_COOKIE_CHUNK_SIZE) {
    parts.push(payload.slice(i, i + MAX_COOKIE_CHUNK_SIZE));
  }

  setCookie(COOKIE_CHUNKS_NAME, String(parts.length), COOKIE_MAX_AGE_SECONDS);
  for (let i = 0; i < parts.length; i++) {
    setCookie(`${COOKIE_CHUNK_PREFIX}${i}`, parts[i], COOKIE_MAX_AGE_SECONDS);
  }
};

/**
 * Écrit un cookie avec les options de sécurité appropriées.
 */
const setCookie = (name, value, maxAgeSeconds) => {
  if (typeof document === 'undefined') return;

  // Vérifier si nous sommes en HTTPS (uniquement côté client)
  const isSecure = typeof window !== 'undefined' && 
                   typeof window.location !== 'undefined' && 
                   window.location.protocol === 'https:';
  const parts = [
    `${name}=${value}`,
    `path=/`,
    `max-age=${maxAgeSeconds}`,
    `SameSite=Lax`,
  ];

  if (isSecure) {
    parts.push('Secure');
  }
  if (shouldUseSharedCookieDomain()) {
    parts.push(`Domain=${SHARED_COOKIE_DOMAIN}`);
  }

  document.cookie = parts.join('; ');
};

/**
 * Supprime un cookie en le mettant à expiration immédiate.
 */
const deleteCookie = (name) => {
  if (typeof document === 'undefined') return;

  const isSecure = typeof window !== 'undefined' &&
                   typeof window.location !== 'undefined' &&
                   window.location.protocol === 'https:';

  const baseParts = [
    `${name}=`,
    'path=/',
    'max-age=0',
    'SameSite=Lax',
  ];

  if (isSecure) {
    baseParts.push('Secure');
  }

  document.cookie = baseParts.join('; ');

  if (shouldUseSharedCookieDomain()) {
    document.cookie = [...baseParts, `Domain=${SHARED_COOKIE_DOMAIN}`].join('; ');
  }
};

/**
 * Adaptateur de stockage compatible avec l'interface Supabase Auth `storage`.
 * Implémente getItem, setItem, removeItem comme localStorage mais via cookies.
 */
const cookieStorageAdapter = {
  getItem: (key) => {
    try {
      const raw = readCookiePayload();
      if (!raw) return null;

      const store = JSON.parse(decodeValue(raw));
      const value = store[key];
      if (value === undefined) return null;

      // Compat : on stocke désormais des objets JSON (plus compact),
      // mais l'ancien format stockait des strings JSON.
      if (typeof value === 'string') return value;
      return JSON.stringify(value);
    } catch (e) {
      console.warn('[CookieStorage] Erreur lecture cookie:', e);
      // Cookie corrompu / tronqué : on le supprime pour repartir proprement
      try {
        deleteChunkCookies();
      } catch {
        // ignore
      }
      return null;
    }
  },

  setItem: (key, value) => {
    try {
      let store = {};
      const raw = readCookiePayload();
      if (raw) {
        try {
          store = JSON.parse(decodeValue(raw));
        } catch {
          store = {};
        }
      }

      // Stocker sous forme d'objet (évite l'échappement d'une string JSON)
      let storedValue = value;
      if (typeof value === 'string') {
        try {
          storedValue = JSON.parse(value);
        } catch {
          storedValue = value;
        }
      }

      store[key] = storedValue;
      const encoded = encodeValue(JSON.stringify(store));
      writeCookiePayload(encoded);
    } catch (e) {
      console.warn('[CookieStorage] Erreur écriture cookie:', e);
    }
  },

  removeItem: (key) => {
    try {
      const raw = readCookiePayload();
      if (!raw) return;

      let store = {};
      try {
        store = JSON.parse(decodeValue(raw));
      } catch {
        // Cookie corrompu : on le supprime
        deleteChunkCookies();
        return;
      }

      delete store[key];

      // Si le store est vide, supprimer le cookie entièrement
      if (Object.keys(store).length === 0) {
        deleteChunkCookies();
      } else {
        const encoded = encodeValue(JSON.stringify(store));
        writeCookiePayload(encoded);
      }
    } catch (e) {
      console.warn('[CookieStorage] Erreur suppression cookie:', e);
    }
  },
};

/**
 * Supprime complètement le cookie de session (utilisé lors du signOut).
 */
export const clearAuthCookie = () => {
  deleteChunkCookies();
};

/**
 * Migre une session existante depuis localStorage vers le cookie.
 * Appelée une seule fois au démarrage pour assurer la transition.
 */
export const migrateLocalStorageToCookie = (supabaseStorageKey) => {
  try {
    if (typeof window === 'undefined') return;

    const existingCookie = readCookiePayload();
    if (existingCookie) {
      if (shouldUseSharedCookieDomain()) {
        writeCookiePayload(existingCookie);
      }
      return; // Déjà migré
    }

    const localData = localStorage.getItem(supabaseStorageKey);
    if (!localData) return; // Rien à migrer

    cookieStorageAdapter.setItem(supabaseStorageKey, localData);

    // Nettoyer l'ancien localStorage
    localStorage.removeItem(supabaseStorageKey);

    console.log('[CookieStorage] Migration localStorage → cookie effectuée');
  } catch (e) {
    console.warn('[CookieStorage] Erreur migration:', e);
  }
};

export default cookieStorageAdapter;
